"use client";

import { z } from "zod";

/**
 * Shared user-field validation primitives. Used by both the public signup
 * flow and the admin "add user" flow so the rules stay in lockstep — if you
 * tweak the password policy here, every place that creates a user picks it
 * up automatically.
 */

type T = (key: string) => string;

export const PASSWORD_MIN_LENGTH = 8;
export const NAME_MIN_LENGTH = 2;
export const NAME_MAX_LENGTH = 80;

export const PASSWORD_RULES = {
  minLength: (v: string) => v.length >= PASSWORD_MIN_LENGTH,
  uppercase: (v: string) => /[A-Z]/.test(v),
  lowercase: (v: string) => /[a-z]/.test(v),
  number: (v: string) => /[0-9]/.test(v),
  specialChar: (v: string) => /[^A-Za-z0-9]/.test(v),
} as const;

export type PasswordRuleKey = keyof typeof PASSWORD_RULES;

export const createEmailSchema = (t: T) =>
  z
    .string()
    .min(1, t("emailRequired"))
    .email(t("emailInvalid"));

export const createNameSchema = (t: T, max: number = NAME_MAX_LENGTH) =>
  z
    .string()
    .trim()
    .min(NAME_MIN_LENGTH, t("nameMinLength"))
    .max(max, t("nameMaxLength"));

/**
 * Required password: must satisfy every rule in PASSWORD_RULES.
 * Each failing rule produces its own message so the form can highlight the
 * specific gap (or feed `PasswordRequirements` for a live checklist).
 */
export const createPasswordSchema = (t: T) =>
  z
    .string({ required_error: t("passwordRequired") })
    .min(PASSWORD_MIN_LENGTH, t("passwordMinLength"))
    .regex(/[A-Z]/, t("passwordUppercase"))
    .regex(/[a-z]/, t("passwordLowercase"))
    .regex(/[0-9]/, t("passwordNumber"))
    .regex(/[^A-Za-z0-9]/, t("passwordSpecialChar"));

/**
 * Optional password (admin-create flow): empty string is allowed (user will
 * recover later). When non-empty, the full password schema applies and
 * preserves per-rule error messages.
 */
export const createOptionalPasswordSchema = (t: T) => {
  const full = createPasswordSchema(t);
  return z.string().superRefine((value, ctx) => {
    if (value === "") return;
    const result = full.safeParse(value);
    if (!result.success) {
      result.error.issues.forEach((issue) => ctx.addIssue(issue));
    }
  });
};
